import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import authApi from "../services/auth.api.js";
import { rememberLogin } from "../utils/savedLogins.js";
import {
  AUTH_EVENTS,
  clearAuth,
  emit,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
} from "../services/token.js";

const AuthContext = createContext(null);

/** Strip the token out of the user object the backend hands back. */
const toProfile = ({ token: _token, ...user }) => user;

/**
 * Honour the "Save Login Info on this Device" preference: when enabled (the
 * default), remember enough identity for a one-tap chip on the next visit.
 * Never stores the password or the session token.
 */
const rememberProfileIfEnabled = (profile) => {
  if (!profile || profile.preferences?.saveLoginInfo === false) return;
  rememberLogin({
    email: profile.email,
    name: profile.name,
    role: profile.role,
    avatar: profile.avatar,
  });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (getToken() ? getStoredUser() : null));
  const [status, setStatus] = useState(() =>
    getToken() ? "checking" : "anonymous",
  );

  const signOut = useCallback(({ notifyServer = true } = {}) => {
    clearAuth();
    setUser(null);
    setStatus("anonymous");
    if (notifyServer) {
      // Fire-and-forget: the JWT is the real session, this is just courtesy.
      authApi.logout().catch(() => {});
    }
  }, []);

  /* Revalidate a token restored from sessionStorage on a hard refresh. */
  useEffect(() => {
    if (!getToken()) return undefined;

    let cancelled = false;
    authApi
      .me()
      .then((profile) => {
        if (cancelled) return;
        setStoredUser(profile);
        setUser(profile);
        setStatus("authenticated");
        rememberProfileIfEnabled(profile);
      })
      .catch(() => {
        if (cancelled) return;
        // api.js already cleared the token and emitted the 401 event.
        clearAuth();
        setUser(null);
        setStatus("anonymous");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* The API layer fires this whenever a protected call comes back 401. */
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setStatus("anonymous");
    };
    window.addEventListener(AUTH_EVENTS.unauthorized, onUnauthorized);
    return () =>
      window.removeEventListener(AUTH_EVENTS.unauthorized, onUnauthorized);
  }, []);

  const applySession = useCallback(({ token, ...rest }) => {
    const profile = toProfile(rest);
    setToken(token);
    setStoredUser(profile);
    setUser(profile);
    setStatus("authenticated");
    rememberProfileIfEnabled(profile);
    emit(AUTH_EVENTS.changed, profile);
    return profile;
  }, []);

  /**
   * Patch the active profile (name / email / phone / avatar / preferences) and
   * persist it, so the header, sidebar and any consumer of `useAuth` update
   * instantly after a profile edit — no reload required.
   */
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next =
        typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      setStoredUser(next);
      emit(AUTH_EVENTS.changed, next);
      return next;
    });
  }, []);

  const signIn = useCallback(
    async (credentials) => {
      const data = await authApi.login(credentials);
      return applySession(data);
    },
    [applySession],
  );

  const signUp = useCallback(
    async (payload) => {
      const data = await authApi.register(payload);
      return applySession(data);
    },
    [applySession],
  );

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthenticated: status === "authenticated",
      isChecking: status === "checking",
      signIn,
      signUp,
      signOut,
      updateUser,
    }),
    [user, status, signIn, signUp, signOut, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
